/**
 * Transactions Panel — sortable table with row click → detail panel,
 * add/delete, and search.
 */

import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Loader2,
  ArrowLeftRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Plus,
  Search,
  ChevronRight,
} from "lucide-react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type SortingState,
  type ColumnDef,
} from "@tanstack/react-table";
import { cn } from "@/utils/misc";
import type { AssetTransactionUI } from "./types";
import { TransactionDetailPanel } from "./TransactionDetailPanel";
import {
  useNewOutputBoundary,
  isNewOutput,
} from "../../../_shared/use-new-output-boundary";
import { NewOutputBadge } from "../../../_shared/new-output-badge";

const fmt = (v: number) =>
  `$${v.toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;

/**
 * Direction filter tabs — mirrors the Loans register (All / Granted / Taken)
 * so filtering is consistent across the data-agent panels.
 */
const FILTERS = [
  { key: "all", label: "All" },
  { key: "sales", label: "Sales" },
  { key: "purchases", label: "Purchases" },
] as const;

type FilterKey = (typeof FILTERS)[number]["key"];
const fmtDate = (iso?: string) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

function makeColumns(newSince: number | null): ColumnDef<AssetTransactionUI>[] {
  return [
    {
      accessorKey: "transactionDate",
      header: "Date",
      cell: ({ getValue }) => (
        <span className="text-xs tabular-nums">
          {fmtDate(getValue<string>())}
        </span>
      ),
    },
    {
      accessorKey: "counterpartyName",
      header: "Counterparty",
      filterFn: "includesString",
      cell: ({ row }) => (
        <div>
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium">
              {row.original.counterpartyName}
            </div>
            {isNewOutput(row.original._creationTime, newSince) && (
              <NewOutputBadge />
            )}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {row.original.assetClass}
          </div>
        </div>
      ),
    },
    {
      accessorKey: "assetDescription",
      header: "Asset",
      cell: ({ row }) => (
        <div>
          <div className="line-clamp-1 text-sm">
            {row.original.assetDescription}
          </div>
          {row.original.assetIdentifier && (
            <div className="font-mono text-[11px] text-muted-foreground">
              {row.original.assetIdentifier}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: "direction",
      header: "Dir.",
      cell: ({ getValue }) => {
        const dir = getValue<string>();
        return (
          <Badge
            color={dir === "Sale" ? "red" : "emerald"}
            variant="soft"
            size="sm"
          >
            {dir === "Sale" ? "Sale" : "Purchase"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "amountNet",
      header: "Net Amount",
      cell: ({ row }) => {
        const color =
          row.original.direction === "Sale"
            ? "text-red-700 dark:text-red-400"
            : "text-emerald-700 dark:text-emerald-400";
        return (
          <span
            className={cn(
              "font-mono text-sm font-semibold tabular-nums",
              color,
            )}
          >
            {fmt(row.original.amountNet)}
          </span>
        );
      },
    },
    {
      accessorKey: "agreementStatus",
      header: "Agreement",
      cell: ({ getValue }) => {
        const v = getValue<string>();
        const color: BadgeColor =
          v === "Executed" ? "emerald" : v === "Not Found" ? "red" : "amber";
        return (
          <Badge color={color} variant="soft" size="sm">
            {v}
          </Badge>
        );
      },
    },
    {
      accessorKey: "reviewStatus",
      header: "Review",
      cell: ({ getValue }) => {
        const v = getValue<string>();
        const color: BadgeColor =
          v === "Reviewed"
            ? "emerald"
            : v === "Escalated"
              ? "red"
              : v === "In Review"
                ? "amber"
                : "gray";
        return (
          <Badge color={color} variant="soft" size="sm">
            {v}
          </Badge>
        );
      },
    },
    {
      id: "chevron",
      header: "",
      cell: () => <ChevronRight className="h-4 w-4 text-muted-foreground/40" />,
    },
  ];
}

export function TransactionsPanel({
  jobId,
  taskId,
  isRunning,
  isViewer,
}: {
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  isRunning: boolean;
  isViewer?: boolean;
}) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: "transactionDate", desc: false },
  ]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [filter, setFilter] = useState<FilterKey>("all");
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);

  const { data: allEntries } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listAssetTransactionsForJob,
      { jobId },
    ),
  );

  const addTransaction = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.assetSalesPurchases.publicMutations
        .addManualTransaction,
    ),
  });

  const newSince = useNewOutputBoundary(jobId, "sum_asset_sales");

  const entries = useMemo<AssetTransactionUI[]>(
    () => allEntries ?? [],
    [allEntries],
  );

  const columns = useMemo(() => makeColumns(newSince), [newSince]);

  const stats = useMemo(() => {
    const sales = entries.filter((e) => e.direction === "Sale");
    const purchases = entries.filter((e) => e.direction === "Purchase");
    return {
      total: entries.length,
      salesNet: sales.reduce((s, e) => s + e.amountNet, 0),
      purchasesNet: purchases.reduce((s, e) => s + e.amountNet, 0),
      salesCount: sales.length,
      purchasesCount: purchases.length,
    };
  }, [entries]);

  // Direction pre-filter applied before the table's counterparty search.
  const filtered = useMemo(() => {
    if (filter === "sales")
      return entries.filter((e) => e.direction === "Sale");
    if (filter === "purchases")
      return entries.filter((e) => e.direction === "Purchase");
    return entries;
  }, [entries, filter]);

  const counts = useMemo<Record<FilterKey, number>>(
    () => ({
      all: stats.total,
      sales: stats.salesCount,
      purchases: stats.purchasesCount,
    }),
    [stats],
  );

  const selectedEntry = useMemo(
    () => entries.find((e) => e._id === selectedEntryId) ?? null,
    [entries, selectedEntryId],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (entries.length === 0) {
    if (isRunning)
      return (
        <div className="flex flex-col items-center gap-3 py-16">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          <p className="text-sm text-muted-foreground">
            Extracting transactions...
          </p>
        </div>
      );
    return (
      <div className="flex flex-col items-center gap-4 py-16">
        <div className="rounded-full bg-muted p-3">
          <ArrowLeftRight className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">
          No asset transactions yet.
        </p>
        {!isViewer && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() =>
              addTransaction.mutate(
                { jobId, taskId, counterpartyName: "New Transaction" },
                { onSuccess: (id) => setSelectedEntryId(id as string) },
              )
            }
          >
            <Plus className="h-3.5 w-3.5" /> Add Transaction
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* Card */}
      <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 px-5 pb-4 pt-5">
          <div className="rounded-xl border bg-muted/30 px-4 py-3">
            <p className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Total sales
            </p>
            <p className="text-lg font-semibold tabular-nums text-red-700 dark:text-red-400">
              {fmt(stats.salesNet)}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {stats.salesCount} transactions
            </p>
          </div>
          <div className="rounded-xl border bg-muted/30 px-4 py-3">
            <p className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Total purchases
            </p>
            <p className="text-lg font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
              {fmt(stats.purchasesNet)}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {stats.purchasesCount} transactions
            </p>
          </div>
          <div className="rounded-xl border bg-muted/30 px-4 py-3">
            <p className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Total transactions
            </p>
            <p className="text-lg font-semibold tabular-nums">{stats.total}</p>
          </div>
        </div>

        {/* Search + Add */}
        <div className="flex items-center justify-between gap-3 px-5 pb-4">
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search counterparty..."
              className="h-9 pl-9 text-sm"
            />
          </div>
          {!isViewer && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={addTransaction.isPending}
              onClick={() =>
                addTransaction.mutate(
                  { jobId, taskId, counterpartyName: "New Transaction" },
                  { onSuccess: (id) => setSelectedEntryId(id as string) },
                )
              }
            >
              <Plus className="h-3.5 w-3.5" /> Add Transaction
            </Button>
          )}
        </div>

        {/* Filter tabs */}
        <div className="scrollbar-none flex items-center gap-1 overflow-x-auto border-t border-border px-5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={cn(
                "-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                filter === f.key
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {f.label}
              <span
                className={cn(
                  "ml-1.5 text-xs tabular-nums",
                  filter === f.key
                    ? "text-muted-foreground"
                    : "text-muted-foreground/50",
                )}
              >
                {counts[f.key]}
              </span>
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="min-w-0 flex-1 border-t border-border">
          <div className="overflow-auto">
            <Table className="w-full min-w-[800px]">
              <TableHeader>
                {table.getHeaderGroups().map((hg) => (
                  <TableRow
                    key={hg.id}
                    className="bg-muted/30 hover:bg-transparent"
                  >
                    {hg.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className={cn(
                          "h-10 select-none whitespace-nowrap text-[10px] font-semibold uppercase tracking-wider text-muted-foreground",
                          header.column.getCanSort() && "cursor-pointer",
                          header.id === "amountNet" && "text-right",
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <span className="inline-flex items-center gap-1">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {header.column.getCanSort() &&
                            (header.column.getIsSorted() === "asc" ? (
                              <ArrowUp className="h-3 w-3" />
                            ) : header.column.getIsSorted() === "desc" ? (
                              <ArrowDown className="h-3 w-3" />
                            ) : (
                              <ArrowUpDown className="h-3 w-3 text-muted-foreground/30" />
                            ))}
                        </span>
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className={cn(
                      "cursor-pointer transition-colors",
                      selectedEntryId === row.original._id
                        ? "bg-primary/5"
                        : "hover:bg-muted/50",
                    )}
                    onClick={() => setSelectedEntryId(row.original._id)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          "px-3 py-2.5",
                          cell.column.id === "amountNet" && "text-right",
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Footer hint */}
        <div className="border-t border-border px-5 py-3">
          <p className="text-center text-xs text-muted-foreground">
            Click any row to view details, edit, and see history
          </p>
        </div>
      </div>

      <TransactionDetailPanel
        entry={selectedEntry}
        open={selectedEntry !== null}
        onClose={() => setSelectedEntryId(null)}
        onDeleted={() => setSelectedEntryId(null)}
        isViewer={isViewer}
      />
    </div>
  );
}
